import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";
import DeleteAvailabilityModal from "./DeleteAvailabilityModal";
import "antd/dist/reset.css";
import "./AddAvailabilityModal.css";
import "./AddAvailabilityDrawer.css";

dayjs.extend(customParseFormat);

const WEEKDAY_OPTIONS = [
  { label: "S", value: "sunday" },
  { label: "M", value: "monday" },
  { label: "T", value: "tuesday" },
  { label: "W", value: "wednesday" },
  { label: "T", value: "thursday" },
  { label: "F", value: "friday" },
  { label: "S", value: "saturday" },
];

const END_TYPES = [
  { label: "Never", value: "never" },
  { label: "ON", value: "on" },
];

const PICKER_POPUP_CONTAINER = () => document.body;

const toTimeInputValue = (time) => {
  if (!time) return "";
  if (dayjs.isDayjs(time)) return time.format("HH:mm");
  const raw = String(time).trim();
  if (/^\d{1,2}:\d{2}/.test(raw)) return raw.slice(0, 5).padStart(5, "0");
  return "";
};

const toDayjsTime = (time) => {
  if (!time) return null;
  if (dayjs.isDayjs(time)) return time.isValid() ? time : null;
  const raw = String(time).trim();
  const parsed = dayjs(raw, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  return parsed.isValid() ? parsed : dayjs(`2000-01-01T${raw.slice(0, 5)}`);
};

const toDayjsDate = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value.startOf("day") : null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).startOf("day");
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = dayjs(raw.slice(0, 10), "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }
  const parsed = dayjs(
    raw,
    ["MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"],
    true,
  );
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const formatUsDate = (date) => {
  const d = toDayjsDate(date);
  return d ? d.format("MM/DD/YYYY") : null;
};

const slotRangesOverlap = (dateA, startA, endA, dateB, startB, endB) => {
  const start1 = moment(`${dateA}T${toTimeInputValue(startA)}`);
  let end1 = moment(`${dateA}T${toTimeInputValue(endA)}`);
  if (!end1.isAfter(start1)) end1 = end1.clone().add(1, "day");

  const start2 = moment(`${dateB}T${toTimeInputValue(startB)}`);
  let end2 = moment(`${dateB}T${toTimeInputValue(endB)}`);
  if (!end2.isAfter(start2)) end2 = end2.clone().add(1, "day");

  return start1.isBefore(end2) && start2.isBefore(end1);
};

const EditAvailabilityDrawer = ({
  authToken,
  existingSlots = [],
  fetchAvailability,
  initialData = {},
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  providerId,
}) => {
  const [availabilityName, setAvailabilityName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [endType, setEndType] = useState("never");
  const [endsOn, setEndsOn] = useState(null);
  const [errors, setErrors] = useState({});

  const [confirmModal, setConfirmModal] = useState(null);
  const [updateAll, setUpdateAll] = useState(false);
  const [recurringChangeMode, setRecurringChangeMode] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const seriesId = initialData?.seriesId;
  const slotId = initialData?.id;
  const wasRecurring = Boolean(initialData?.isRecurring || seriesId);

  useEffect(() => {
    if (!isOpen) return;

    const dateValue =
      toDayjsDate(initialData.startDate) || dayjs().startOf("day");
    const start = toDayjsTime(initialData.startTime);
    const end = toDayjsTime(initialData.endTime);
    const weekday = dateValue.format("dddd").toLowerCase();

    setAvailabilityName(
      initialData.title || initialData.name || "My Availability",
    );
    setStartDate(dateValue);
    setStartTime(start);
    setEndTime(end);
    setIsRecurring(Boolean(initialData.isRecurring));
    setRecurringInterval(Number(initialData.recurringInterval) || 1);
    setSelectedDays(
      Array.isArray(initialData.selectedDays) && initialData.selectedDays.length
        ? initialData.selectedDays
        : [weekday],
    );
    setEndType(initialData.endType === "on" ? "on" : "never");
    setEndsOn(toDayjsDate(initialData.endsOn));
    setErrors({});
    setConfirmModal(null);
    setUpdateAll(false);
    setRecurringChangeMode(null);
    setShowDeleteModal(false);
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const dayKey = startDate ? startDate.format("YYYY-MM-DD") : "";
  const startTimeKey = startTime ? startTime.format("HH:mm:ss") : "";
  const endTimeKey = endTime ? endTime.format("HH:mm:ss") : "";

  const durationLabel = (() => {
    if (!startTime || !endTime || !endTime.isAfter(startTime)) return "";
    const mins = endTime.diff(startTime, "minute");
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (!hours) return `${minutes}m`;
    if (!minutes) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  })();

  const toggleDay = (value) => {
    setSelectedDays((prev) =>
      prev.includes(value)
        ? prev.filter((d) => d !== value)
        : [...prev, value],
    );
  };

  const handleStartDateChange = (date) => {
    const next = toDayjsDate(date);
    setStartDate(next);
    if (next) {
      const weekday = next.format("dddd").toLowerCase();
      setSelectedDays((prev) =>
        prev.includes(weekday) ? prev : [...prev, weekday],
      );
    }
  };

  const handleEndTypeChange = (value) => {
    setEndType(value);
    if (value === "on") {
      setEndsOn((prev) => prev || startDate || dayjs().startOf("day"));
    } else {
      setEndsOn(null);
    }
  };

  const checkClientOverlap = () => {
    if (!dayKey || !startTimeKey || !endTimeKey) return null;
    return existingSlots.find(
      (slot) =>
        slot.id !== slotId &&
        slotRangesOverlap(
          dayKey,
          startTimeKey,
          endTimeKey,
          slot.startDate,
          slot.startTime,
          slot.endTime,
        ),
    );
  };

  const validate = () => {
    const next = {};
    if (!availabilityName.trim()) {
      next.availabilityName = "Availability name is required.";
    }
    if (!startDate) next.startDate = "Date is required.";
    if (!startTime) next.startTime = "Start time is required.";
    if (!endTime) next.endTime = "End time is required.";
    if (startTime && endTime && !endTime.isAfter(startTime)) {
      next.endTime = "End time must be after start time.";
    }
    if (isRecurring && selectedDays.length === 0) {
      next.selectedDays = "Select at least one day.";
    }
    if (isRecurring && (recurringInterval < 1 || recurringInterval > 12)) {
      next.recurringInterval = "Interval must be between 1 and 12.";
    }
    if (isRecurring && endType === "on") {
      if (!endsOn) next.endsOn = "End date is required.";
      else if (startDate && endsOn.isBefore(startDate, "day")) {
        next.endsOn = "End date must be on or after the start date.";
      }
    }

    if (Object.keys(next).length === 0) {
      const overlap = checkClientOverlap();
      if (overlap) {
        next.overlap =
          "This availability overlaps with an existing block. Please adjust your hours.";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (applyToAll = false) => {
    const endFutureSeries = recurringChangeMode === "end-future";
    const recurring = endFutureSeries ? false : isRecurring;

    return {
      name: availabilityName.trim(),
      notes: availabilityName.trim() || null,
      startDate: formatUsDate(startDate),
      startTime: startTimeKey,
      endTime: endTimeKey,
      isRecurring: recurring,
      recurringInterval: recurring ? Number(recurringInterval) || 1 : 1,
      recurringUnit: "week",
      selectedDays: recurring
        ? selectedDays
        : [startDate.format("dddd").toLowerCase()],
      endType: endFutureSeries ? "on" : recurring ? endType : "never",
      endsOn: endFutureSeries
        ? formatUsDate(startDate)
        : recurring && endType === "on" && endsOn
          ? formatUsDate(endsOn)
          : null,
      endOccurrences: null,
      timezone: moment.tz.guess(),
      updateAll: applyToAll || endFutureSeries,
      seriesId,
    };
  };

  const commitSave = (applyToAll) => {
    onSubmit(buildPayload(applyToAll));
    setConfirmModal(null);
  };

  const handleRecurringChange = (checked) => {
    if (!checked && seriesId && isRecurring) {
      setRecurringChangeMode("single-only");
      setConfirmModal("recurring-to-onetime");
      return;
    }
    setIsRecurring(checked);
    setRecurringChangeMode(null);
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (recurringChangeMode === "end-future") {
      setConfirmModal("save-end-future");
      return;
    }

    if (recurringChangeMode === "single-only") {
      setConfirmModal("save");
      setUpdateAll(false);
      return;
    }

    if (seriesId && isRecurring) {
      setConfirmModal("update-scope");
      return;
    }

    setConfirmModal("save");
  };

  const deleteEventData = {
    id: slotId,
    seriesId,
    isRecurring: wasRecurring,
    startDate: dayKey,
    endDate: dayKey,
    startTime: startTimeKey,
    endTime: endTimeKey,
    title: availabilityName.trim() || "Available",
  };

  return createPortal(
    <>
      <div className="pp-availability-drawer-overlay" onClick={onClose}>
        <aside
          className="pp-availability-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-edit-availability-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pp-availability-drawer-header">
            <h2 id="pp-edit-availability-title">Edit Availability</h2>
            <button
              type="button"
              className="pp-availability-drawer-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes size={14} />
            </button>
          </div>

          <form className="pp-availability-drawer-form" onSubmit={handleSaveClick}>
            <div className="pp-availability-drawer-body availability-form">
              <div className="form-section">
                <label className="form-label" htmlFor="pp-edit-avail-name">
                  Availability Name
                </label>
                <input
                  id="pp-edit-avail-name"
                  type="text"
                  className={`form-input ${
                    errors.availabilityName ? "error" : ""
                  }`}
                  value={availabilityName}
                  onChange={(e) => setAvailabilityName(e.target.value)}
                />
                {errors.availabilityName && (
                  <span className="error-message">
                    {errors.availabilityName}
                  </span>
                )}
              </div>

              <div className="form-section">
                <div className="datetime-range pp-availability-datetime-range">
                  <div className="datetime-input">
                    <div className="input-wrapper date-picker">
                      <DatePicker
                        className="pp-availability-antd-date"
                        format="MM/DD/YYYY"
                        value={startDate}
                        onChange={handleStartDateChange}
                        placeholder="Select date"
                        allowClear={false}
                        inputReadOnly
                        getPopupContainer={PICKER_POPUP_CONTAINER}
                        placement="bottomLeft"
                      />
                    </div>
                    <div className="bar" />
                    <div className="input-wrapper time-picker">
                      <TimePicker
                        format="h:mm A"
                        minuteStep={15}
                        use12Hours
                        value={startTime}
                        onChange={setStartTime}
                        placeholder="Start time"
                        allowClear={false}
                        needConfirm={false}
                        getPopupContainer={PICKER_POPUP_CONTAINER}
                        placement="bottomLeft"
                      />
                    </div>
                  </div>

                  <span className="range-separator">to</span>

                  <div className="datetime-input">
                    <div className="input-wrapper time-picker">
                      <TimePicker
                        format="h:mm A"
                        minuteStep={15}
                        use12Hours
                        value={endTime}
                        onChange={setEndTime}
                        placeholder="End time"
                        allowClear={false}
                        needConfirm={false}
                        getPopupContainer={PICKER_POPUP_CONTAINER}
                        placement="bottomLeft"
                      />
                    </div>
                  </div>
                </div>
                {(errors.startDate || errors.startTime || errors.endTime) && (
                  <span className="error-message">
                    {errors.startDate || errors.startTime || errors.endTime}
                  </span>
                )}
              </div>

              <div className="form-section">
                <label className="form-label" htmlFor="pp-edit-avail-duration">
                  Duration
                </label>
                <input
                  id="pp-edit-avail-duration"
                  type="text"
                  className="form-input pp-availability-duration-input"
                  value={durationLabel || "—"}
                  readOnly
                  tabIndex={-1}
                />
              </div>

              <div className="form-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={isRecurring}
                    onChange={(e) => handleRecurringChange(e.target.checked)}
                  />
                  <span className="checkbox-text">Recurring Availability</span>
                </label>
              </div>

              {isRecurring && (
                <div className="recurring-section">
                  <div className="recurring-row">
                    <span className="recurring-label">Every</span>
                    <select
                      className={`form-select recurring-number ${
                        errors.recurringInterval ? "error" : ""
                      }`}
                      value={recurringInterval}
                      onChange={(e) =>
                        setRecurringInterval(Number(e.target.value))
                      }
                    >
                      {[1, 2, 3, 4].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-select is-static"
                      value="week"
                      disabled
                    >
                      <option value="week">Week</option>
                    </select>
                  </div>
                  {errors.recurringInterval && (
                    <span className="error-message">
                      {errors.recurringInterval}
                    </span>
                  )}

                  <div className="days-selection">
                    {WEEKDAY_OPTIONS.map((opt, index) => (
                      <button
                        key={`${opt.value}-${index}`}
                        type="button"
                        className={`day-button ${
                          selectedDays.includes(opt.value) ? "selected" : ""
                        }`}
                        onClick={() => toggleDay(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {errors.selectedDays && (
                    <span className="error-message">{errors.selectedDays}</span>
                  )}

                  <div className="recurring-row pp-availability-ends-row">
                    <span className="recurring-label">Ends</span>
                    <select
                      className="form-select ends-on-select"
                      value={endType}
                      onChange={(e) => handleEndTypeChange(e.target.value)}
                      aria-label="Recurring end condition"
                    >
                      {END_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {endType === "on" && (
                      <div className="pp-availability-ends-date-wrap">
                        <DatePicker
                          className="pp-availability-ends-date"
                          format="MM/DD/YYYY"
                          value={endsOn}
                          onChange={(date) => setEndsOn(toDayjsDate(date))}
                          placeholder="Select end date"
                          allowClear={false}
                          inputReadOnly
                          getPopupContainer={PICKER_POPUP_CONTAINER}
                          placement="bottomLeft"
                          disabledDate={(current) =>
                            startDate
                              ? current && current.isBefore(startDate, "day")
                              : false
                          }
                        />
                      </div>
                    )}
                  </div>
                  {errors.endsOn && (
                    <span className="error-message">{errors.endsOn}</span>
                  )}
                </div>
              )}

              {errors.overlap && (
                <span className="error-message">{errors.overlap}</span>
              )}

              <button
                type="button"
                className="pp-availability-remove-btn"
                onClick={() => setShowDeleteModal(true)}
                disabled={isSubmitting}
              >
                Remove availability
              </button>
            </div>

            <div className="pp-availability-drawer-footer">
              <button
                type="button"
                className="pp-availability-btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pp-availability-btn-save"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </aside>
      </div>

      {confirmModal === "save" && (
        <div className="modal-overlay pp-confirm-overlay pp-availability-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
            role="dialog"
            aria-modal="true"
          >
            <h3 className="pp-confirm-title">Save changes?</h3>
            <p className="pp-confirm-body">
              Your updates will be applied to this availability block.
            </p>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove pp-availability-confirm-save"
                onClick={() => commitSave(updateAll)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={() => setConfirmModal(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal === "save-end-future" && (
        <div className="modal-overlay pp-confirm-overlay pp-availability-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
            role="dialog"
            aria-modal="true"
          >
            <h3 className="pp-confirm-title">Save changes?</h3>
            <p className="pp-confirm-body">
              This will convert this block to a one-time availability and remove
              all future instances in the series.
            </p>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove pp-availability-confirm-save"
                onClick={() => commitSave(true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={() => setConfirmModal(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal === "update-scope" && (
        <div className="modal-overlay pp-confirm-overlay pp-availability-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
            role="dialog"
            aria-modal="true"
          >
            <h3 className="pp-confirm-title">Apply changes</h3>
            <p className="pp-confirm-body">
              This availability is part of a recurring series. How would you
              like to apply your changes?
            </p>
            <div className="pp-availability-confirm-options">
              <label className="pp-availability-confirm-option">
                <input
                  type="radio"
                  name="update-scope"
                  checked={!updateAll}
                  onChange={() => setUpdateAll(false)}
                />
                <span>This availability only</span>
              </label>
              <label className="pp-availability-confirm-option">
                <input
                  type="radio"
                  name="update-scope"
                  checked={updateAll}
                  onChange={() => setUpdateAll(true)}
                />
                <span>Entire series</span>
              </label>
            </div>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove pp-availability-confirm-save"
                onClick={() => commitSave(updateAll)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={() => setConfirmModal(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal === "recurring-to-onetime" && (
        <div className="modal-overlay pp-confirm-overlay pp-availability-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
            role="dialog"
            aria-modal="true"
          >
            <h3 className="pp-confirm-title">Change to one-time</h3>
            <p className="pp-confirm-body">
              This availability is part of a series. What would you like to do?
            </p>
            <div className="pp-availability-confirm-options">
              <label className="pp-availability-confirm-option">
                <input
                  type="radio"
                  name="recurring-change"
                  checked={recurringChangeMode !== "end-future"}
                  onChange={() => setRecurringChangeMode("single-only")}
                />
                <span>
                  This availability only
                  <small>Removes this block from the recurring series</small>
                </span>
              </label>
              <label className="pp-availability-confirm-option">
                <input
                  type="radio"
                  name="recurring-change"
                  checked={recurringChangeMode === "end-future"}
                  onChange={() => setRecurringChangeMode("end-future")}
                />
                <span>
                  Delete all future instances
                  <small>Ends the series after this block</small>
                </span>
              </label>
            </div>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove pp-availability-confirm-save"
                onClick={() => {
                  if (!recurringChangeMode) return;
                  setIsRecurring(false);
                  setConfirmModal(null);
                }}
                disabled={!recurringChangeMode}
              >
                Continue
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={() => {
                  setRecurringChangeMode(null);
                  setConfirmModal(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeleteAvailabilityModal
          authToken={authToken}
          calendarRef={{ current: null }}
          deleteEventData={deleteEventData}
          fetchAvailability={async () => {
            await fetchAvailability();
            onClose();
          }}
          onClose={() => setShowDeleteModal(false)}
          overlayClassName="pp-availability-confirm-overlay"
          providerId={providerId}
        />
      )}
    </>,
    document.body,
  );
};

export default EditAvailabilityDrawer;
